import React from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { getDownloadURL } from 'firebase/storage';
import { uploadFile } from '../data/uploadFile';
import { dataURItoBlob } from '../data/dataURItoBlob';
import { useHandleFileUploadSuccess } from '../hooks/useHandleFileUploadSuccess';
import { SignatureBoxProps } from '../types/Signatures/SignatureBoxProps';
import { FileUpload } from '../types/Files/FileUpload';
import { Button } from './buttons/Button';
import { useOrientation, useWindowSize } from '@uidotdev/usehooks';
import * as Sentry from '@sentry/browser';
import { ButtonGreen } from './buttons';

export const SignatureBox = (props: SignatureBoxProps) => {
  const sigRef = React.useRef<SignatureCanvas | null>(null);
  const orientation = useOrientation();
  const size = useWindowSize();
  const { updateFirestore } = useHandleFileUploadSuccess();

  const handleImageUpload = () => {
    // CHECK FOR EMPTY CANVAS
    if (sigRef && sigRef.current && sigRef.current.isEmpty()) {
      alert('Please sign before saving');
      return;
    }
    // GET THE IMAGE SRC FROM THE CANVAS AND UPLOAD
    const imageSrc = sigRef?.current?.getTrimmedCanvas().toDataURL('image/png') as string;
    const file = dataURItoBlob(imageSrc);
    const payload = {
      bucket: props.bucket,
      path: props.path,
      file,
    } as FileUpload;
    const uploadTask = uploadFile(payload);
    if (!uploadTask) {
      Sentry.captureMessage('Unable to create upload task');
      console.error('Unable to create upload task');
      props.hideSigBox();
      alert(
        'Unable to connect to upload server. This is likely due to your current network quality or configuration. Please try again from your personal network without a VPN. If this problem persists, please notify support.',
      );
      return;
    }
    uploadTask.on(
      'state_changed',
      (snapshot) => {
        // Get task progress, including the number of bytes uploaded and the total number of bytes to be uploaded
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        console.info('Upload is ' + progress + '% done');
      },
      (error) => {
        Sentry.captureException(error);
        console.error(error);
        alert(`Error uploading file: ${error.message}`);
      },
      () => {
        // UPLOAD SUCCESSFUL
        getDownloadURL(uploadTask.snapshot.ref)
          .then((downloadURL) => {
            // SAVE THE DOWNLOAD URL TO FIRESTORE
            if (downloadURL) {
              const payload = {
                docToUpdate: props.docToUpdate,
                patientId: props.patientId,
                type: props.type,
                url: downloadURL,
              };
              updateFirestore(payload);
              props.hideSigBox();
            }
          })
          .catch((error) => {
            Sentry.captureException(error);
            console.error('Error getting download URL:', error);
          });
      },
    );
  };

  const handleClear = () => {
    if (sigRef && sigRef.current) {
      sigRef.current.clear();
    }
  };

  // MOBILE FIRST, THEN ADJUST SIGNATURE PAD SIZE BASED ON SCREEN WIDTH
  // @ts-expect-error: unknown function on global (we are defining it here)
  window.mobileCheck = function () {
    let check = false;
    (function (a) {
      if (
        //spellchecker:disable
        /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
          a,
        ) ||
        // eslint-disable-next-line no-useless-escape
        /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
          a.substr(0, 4),
        )
        //spellchecker:enable
      )
        check = true;
      // @ts-expect-error: opera not found on window global
    })(navigator.userAgent || navigator.vendor || window.opera);
    return check;
  };
  // @ts-expect-error: unknown function on global - defined above
  const isMobile = window.mobileCheck();

  // const sigDiv = document.getElementById(props.elementId + 'box');
  // const box = sigDiv?.getBoundingClientRect();

  return (
    <div>
      <div className="w-full outline" id={props.elementId + 'box'}>
        {orientation.type.includes('portrait') ? (
          <SignatureCanvas
            ref={sigRef}
            backgroundColor="#ffffff"
            canvasProps={{ height: 125, width: window.screen.width * 0.75, id: 'sig-canvas' }}
          />
        ) : isMobile ? (
          <SignatureCanvas
            ref={sigRef}
            backgroundColor="#ffffff"
            canvasProps={{ height: 125, width: window.screen.width * 1.4, id: 'sig-canvas' }}
          />
        ) : (
          <SignatureCanvas
            ref={sigRef}
            backgroundColor="#ffffff"
            canvasProps={{ height: 125, width: size.width ? size.width * 0.8 : 900, id: 'sig-canvas' }}
          />
        )}
      </div>
      <ButtonGreen
        id={props.elementId + 'save-signature'}
        type="button"
        onClick={handleImageUpload}
        textContent="Save Signature"
      />
      <Button id={props.elementId + 'clear-signature'} type="button" onClick={handleClear} textContent="Clear" />
      <Button
        id={props.elementId + 'close-without-save'}
        type="button"
        onClick={props.hideSigBox}
        textContent="Close Without Saving"
      />
    </div>
  );
};
